import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useContext, useEffect } from 'react'
import { BefehlFormAuftrag, BefehlFormField } from '../Generic'
import { Input, TextArea } from '../../form'
import GrundDialogContext from '../../../contexts/GrundDialogContext'

export interface Auftrag6_90FormProps {
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings?: boolean
}

export function Auftrag6_90Form({
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag6_90FormProps>) {
  const formMethods = useFormContext()
  const { control, formState, watch, setValue, getValues } = formMethods
  const { errors } = formState
  const { t } = useTranslation('ui-befehle')

  const { openGrundDialog, submittedGruende, resetGruende } =
    useContext(GrundDialogContext)

  const x91FreitextFieldName = 'auftrag_6_90.x_91_freitext'

  const isAuftragEnabled = watch('auftrag_6_90.enabled')

  const submittedGrundeForFreitext = submittedGruende[x91FreitextFieldName]

  useEffect(() => {
    if (isAuftragEnabled) {
      if (!readOnly) {
        if (submittedGrundeForFreitext || submittedGrundeForFreitext === '') {
          setValue(x91FreitextFieldName, submittedGrundeForFreitext)
        }
      }
    }
  }, [isAuftragEnabled, setValue, readOnly, submittedGrundeForFreitext])

  return (
    <BefehlFormAuftrag
      auftragKennung="6.90"
      displayKennung={isAuftragEnabled ? '6.90' : 'x.90'}
      fieldName="auftrag_6_90.enabled"
      control={control}
      readOnly={readOnly}
      testId={testId}
      onChange={(checked) => {
        if (!checked) {
          setValue('auftrag_6_90.x_91_freitext', '')
          resetGruende(x91FreitextFieldName)
          setValue('auftrag_6_90.x_92_freitext', '')
        }
      }}
    >
      <span data-testid={`${testId}-text`}>{t('auftraege.x_90.label')}</span>
      <div className="flex-1">
        <div className="flex flex-col gap-1">
          <div className="flex flex-col">
            <BefehlFormField
              name="auftrag_6_90.x_91_freitext"
              control={control}
              label=""
              readOnly={readOnly}
              disabled={!isAuftragEnabled}
              activateEmptyWarning={activateEmptyFieldWarnings}
              className="w-full"
              data-testid={`${testId}-feld-6-91`}
              isTextArea={true}
              render={({ field, isDisabled }) => (
                <TextArea
                  {...field}
                  label=""
                  rows={4}
                  disabled={isDisabled}
                  readOnly={true}
                  errors={errors}
                  className="w-full"
                  data-testid={`${testId}-feld-6-91-inner`}
                  onClick={() =>
                    openGrundDialog({
                      gruende: getValues(x91FreitextFieldName),
                      fieldName: x91FreitextFieldName
                    })
                  }
                />
              )}
            />
            <span
              data-testid={`${testId}-feld-6-91-label`}
              className={isAuftragEnabled ? '' : 'text-gray-500 opacity-60'}
            >
              <span>{isAuftragEnabled ? '6' : 'x'}.91</span>{' '}
              <span className="italic">[{t('auftraege.x_90.x_91.label')}]</span>
            </span>
          </div>

          <div className="flex flex-col gap-0.5 mt-0.5">
            <span data-testid={`${testId}-text-ergebnis-melden-an`}>
              {t('auftraege.6_40_fields.ergebnis_melden_an')}
            </span>
          </div>

          <div className="flex flex-col">
            <BefehlFormField
              name="auftrag_6_90.x_92_freitext"
              control={control}
              label={t('auftraege.6_90.meldeempfaenger_label')}
              readOnly={readOnly}
              disabled={!isAuftragEnabled}
              activateEmptyWarning={activateEmptyFieldWarnings}
              className="w-full"
              data-testid={`${testId}-feld-6-92`}
              render={({ field, isDisabled, isReadOnly }) => (
                <Input
                  {...field}
                  label={t('auftraege.6_90.meldeempfaenger_label')}
                  disabled={isDisabled}
                  readOnly={isReadOnly}
                  errors={errors}
                  className="w-full"
                  data-testid={`${testId}-feld-6-92-inner`}
                />
              )}
            />
            <span
              data-testid={`${testId}-feld-6-92-label`}
              className={isAuftragEnabled ? '' : 'text-gray-500 opacity-60'}
            >
              <span>{isAuftragEnabled ? '6' : 'x'}.92</span>{' '}
              <span className="italic">[{t('auftraege.x_90.x_92.label')}]</span>
            </span>
          </div>
        </div>
      </div>
    </BefehlFormAuftrag>
  )
}
